import React, { useEffect, useMemo, useState } from "react";
import { Tag, FileText, FileSignature } from "lucide-react";
import api, { formatApiError, downloadFromApi } from "../lib/api";
import Modal from "./Modal";
import BlankPane from "./loadtags/BlankPane";
import RpBasedPane from "./loadtags/RpBasedPane";
import ManualPane from "./loadtags/ManualPane";

const todayIso = () => new Date().toISOString().slice(0, 10);
const newUid = () =>
    (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `g-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const TABS = [
    { key: "rp_based", label: "From RP", icon: Tag },
    { key: "manual", label: "Manual / Advance", icon: FileSignature },
    { key: "blank", label: "Blank", icon: FileText },
];

export default function LoadTagsModal({ order, onClose, onGenerated }) {
    const [tab, setTab] = useState("rp_based");
    const [rpEntries, setRpEntries] = useState([]);
    const [selectedRpIds, setSelectedRpIds] = useState(new Set());
    const [copiesPerPallet, setCopiesPerPallet] = useState(1);
    const [blankCount, setBlankCount] = useState(4);

    // Manual mode
    const activeLines = useMemo(
        () => (order.lines || []).filter((l) => l.active),
        [order],
    );
    const [orderLineId, setOrderLineId] = useState(activeLines[0]?.id || "");
    const [productionDate, setProductionDate] = useState(todayIso());
    const [palletGroups, setPalletGroups] = useState([
        { _uid: newUid(), pallets: 1, units_per_pallet: 0, copies: 1 },
    ]);

    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get(`/orders/${order.id}/rp`)
            .then((r) => {
                setRpEntries(r.data);
                setSelectedRpIds(new Set(r.data.map((rp) => rp.id)));
            })
            .catch((err) => {
                console.error("LT modal: failed to load RP entries", err);
            });
    }, [order.id]);

    const toggleRp = (id) =>
        setSelectedRpIds((prev) => {
            const s = new Set(prev);
            if (s.has(id)) s.delete(id);
            else s.add(id);
            return s;
        });

    const rpPalletPreview = useMemo(() => {
        let total = 0;
        let units = 0;
        for (const rp of rpEntries) {
            if (!selectedRpIds.has(rp.id)) continue;
            total += rp.total_pallets;
            units += rp.total_units;
        }
        return {
            pallets: total * copiesPerPallet,
            base_pallets: total,
            units,
        };
    }, [rpEntries, selectedRpIds, copiesPerPallet]);

    const manualPalletPreview = useMemo(() => {
        let pages = 0;
        let units = 0;
        for (const g of palletGroups) {
            const p = Number(g.pallets) || 0;
            const c = Number(g.copies) || 1;
            const u = Number(g.units_per_pallet) || 0;
            pages += p * c;
            units += p * u;
        }
        return { pages, units };
    }, [palletGroups]);

    const submit = async () => {
        setError("");
        setBusy(true);
        try {
            let payload = {};
            if (tab === "blank") {
                if (!orderLineId) {
                    setError("Select an order line / product before generating blank Load Tags.");
                    setBusy(false);
                    return;
                }
                payload = {
                    mode: "blank",
                    blank_count: blankCount,
                    order_line_id: orderLineId,
                };
            } else if (tab === "rp_based") {
                if (rpEntries.length === 0) {
                    setError("This order has no RP entries yet. Use Manual or Blank.");
                    setBusy(false);
                    return;
                }
                payload = {
                    mode: "rp_based",
                    rp_ids: Array.from(selectedRpIds),
                    copies_per_pallet: Number(copiesPerPallet),
                };
            } else {
                if (!orderLineId) {
                    setError("Select an order line.");
                    setBusy(false);
                    return;
                }
                for (const g of palletGroups) {
                    if (!Number(g.pallets) || !Number(g.units_per_pallet)) {
                        setError(
                            "Each pallet group needs pallets ≥ 1 and units per pallet ≥ 1.",
                        );
                        setBusy(false);
                        return;
                    }
                }
                payload = {
                    mode: "manual",
                    order_line_id: orderLineId,
                    production_date: productionDate,
                    pallet_groups: palletGroups.map((g) => ({
                        pallets: Number(g.pallets),
                        units_per_pallet: Number(g.units_per_pallet),
                        copies: Number(g.copies) || 1,
                    })),
                };
            }

            // Round-26 — Blank LT is now a real persisted document like
            // rp_based and manual. Server returns a doc row; we download via
            // the standard documents endpoint.
            const { data: doc } = await api.post(
                `/orders/${order.id}/load-tags`,
                payload,
            );
            try {
                await downloadFromApi(`/documents/${doc.id}/download`, doc.filename);
            } catch (dlErr) {
                console.error("LT post-generation download failed", dlErr);
            }
            onGenerated?.(doc);
        } catch (err) {
            // Axios error shape: err.response.data may be a blob when responseType=blob
            let msg = err.message;
            const detail = err?.response?.data;
            if (detail && typeof detail === "object" && !(detail instanceof Blob)) {
                msg = formatApiError(detail.detail) || msg;
            } else if (detail instanceof Blob) {
                try {
                    const txt = await detail.text();
                    const j = JSON.parse(txt);
                    msg = formatApiError(j?.detail) || msg;
                } catch (parseErr) {
                    console.error("LT error: failed to parse blob error body", parseErr);
                }
            }
            setError(msg);
        } finally {
            setBusy(false);
        }
    };

    const addGroup = () =>
        setPalletGroups((g) => [
            ...g,
            { _uid: newUid(), pallets: 1, units_per_pallet: 0, copies: 1 },
        ]);
    const updateGroup = (idx, patch) =>
        setPalletGroups((g) => g.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    const removeGroup = (idx) =>
        setPalletGroups((g) => g.filter((_, i) => i !== idx));

    return (
        <Modal
            title={`Load Tags · ${order.internal_number}`}
            onClose={onClose}
            size="lg"
            testId="lt-modal"
        >
            <div className="space-y-4">
                <div className="inline-flex border border-gray-200 rounded-sm p-1 w-full sm:w-auto">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className={`flex-1 sm:flex-none inline-flex items-center gap-1 px-4 py-2 text-sm font-semibold rounded-sm ${
                                tab === t.key
                                    ? "bg-[var(--odr-red)] text-white"
                                    : "text-gray-700 hover:bg-gray-50"
                            }`}
                            data-testid={`lt-tab-${t.key}`}
                        >
                            <t.icon className="w-4 h-4" /> {t.label}
                        </button>
                    ))}
                </div>

                {tab === "blank" && (
                    <BlankPane
                        blankCount={blankCount}
                        onBlankCountChange={setBlankCount}
                        activeLines={activeLines}
                        orderLineId={orderLineId}
                        onOrderLineChange={setOrderLineId}
                    />
                )}

                {tab === "rp_based" && (
                    <RpBasedPane
                        rpEntries={rpEntries}
                        selectedRpIds={selectedRpIds}
                        onToggleRp={toggleRp}
                        copiesPerPallet={copiesPerPallet}
                        onCopiesChange={setCopiesPerPallet}
                        palletPreview={rpPalletPreview}
                    />
                )}

                {tab === "manual" && (
                    <ManualPane
                        activeLines={activeLines}
                        orderLineId={orderLineId}
                        onOrderLineChange={setOrderLineId}
                        productionDate={productionDate}
                        onProductionDateChange={setProductionDate}
                        palletGroups={palletGroups}
                        onAddGroup={addGroup}
                        onUpdateGroup={updateGroup}
                        onRemoveGroup={removeGroup}
                        palletPreview={manualPalletPreview}
                    />
                )}

                {error && (
                    <div className="text-sm border border-red-200 bg-red-50 text-red-700 rounded-sm px-3 py-2">
                        {error}
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
                    <button
                        onClick={onClose}
                        className="odr-btn rounded-sm border border-gray-300 px-4 text-sm text-gray-700 hover:bg-gray-50"
                        data-testid="lt-cancel"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={submit}
                        disabled={busy}
                        className="odr-btn rounded-sm bg-[var(--odr-red)] hover:bg-[var(--odr-red-hover)] text-white text-sm font-semibold px-4 disabled:opacity-60"
                        data-testid="lt-submit"
                    >
                        {busy ? "Generating…" : "Generate & Download"}
                    </button>
                </div>
            </div>
        </Modal>
    );
}
